import React, { useMemo, useCallback } from 'react';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import EmptyAddCard from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/empty/components/empty-add-card/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { isMobileAndInMvpOrExistingUsersExperiment } from '@atlassian/jira-mobile-web/src/index.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { YourWorkItemListSkeleton } from '@atlassian/jira-skeletons/src/ui/your-work/YourWorkItemListSkeleton.tsx';
import {
	MAX_ITEMS_PER_PAGE,
	WORKEDON_TAB_KEY,
	SUCCESS_STATE,
	ERROR_STATE,
	EMPTY_STATE,
} from '../../../common/constants/tab-content.tsx';
import type { ItemProviderDataSection } from '../../../common/types/item-provider.tsx';
import type { TabProps } from '../../../common/types/tabs.tsx';
import EmptyState from '../../../common/ui/empty-state/index.tsx';
import ErrorState from '../../../common/ui/error-state/index.tsx';
import ItemList from '../../../common/ui/item-list/index.tsx';
import ReloadingSpinner from '../../../common/ui/reloading-spinner/index.tsx';
import { TabFooter } from '../../../common/ui/tab-footer/styled.tsx';
import {
	ButtonWithAnalytics,
	LinkWithAnalytics,
} from '../../../common/ui/with-analytics/index.tsx';
import {
	trimItemsBySection,
	EndInteractionsTracker,
	useFireErrorOrSuccessAnalytics,
} from '../../../common/utils.tsx';
import RecentlyViewedImageUrl from './assets/migration-logo.svg';
import messages from './messages.tsx';

export const sumLength = (initialLength: number, section: ItemProviderDataSection) =>
	initialLength + section.items.length;

const WorkedOnTab = (props: TabProps) => {
	const emptyStateDescriptionIssueTermRefreshGalaxia = expVal(
		'project-terminology-refresh',
		'isEnabled',
		false,
	)
		? messages.emptyStateDescriptionIssueTermRefreshGalaxia
		: messages.emptyStateDescriptionIssueTermRefresh;
	const viewAllProjectsGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? messages.viewAllProjectsGalaxia
		: messages.viewAllProjects;
	const {
		itemProviderResult: { data, error, loading, reloading },
		onLoadMoreClick,
	} = props;

	const { formatMessage } = useIntl();

	useFireErrorOrSuccessAnalytics(!!data.total, error, loading, 'workedOn');

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const analyticsEvent = useMemo(
		() =>
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'link',
			}),
		[createAnalyticsEvent],
	);

	const handleClick = useCallback(() => {
		// BLU-1095
		fireUIAnalytics(analyticsEvent, 'recentWorkItem', { pageLocation: 'home' });
	}, [analyticsEvent]);

	if (error && !data.total) {
		return (
			<>
				<ErrorState />
				<EndInteractionsTracker transitionKey={WORKEDON_TAB_KEY} renderState={ERROR_STATE} />
			</>
		);
	}

	if (loading) {
		return <YourWorkItemListSkeleton />;
	}

	if (data.sections.length === 0 && isMobileAndInMvpOrExistingUsersExperiment()) {
		return (
			<>
				<EmptyState
					header={formatMessage(messages.emptyStateHeader)}
					description={formatMessage(messages.emptyStateDescriptionMobile)}
					renderImage={() => (
						// renderImage doesn't add an image wrapper whereas imageUrl does
						<Box paddingBlockEnd="space.100">
							<Inline alignInline="center">
								<EmptyAddCard alt="" width={154} />
							</Inline>
						</Box>
					)}
				/>
				<EndInteractionsTracker transitionKey={WORKEDON_TAB_KEY} renderState={EMPTY_STATE} />
			</>
		);
	}

	if (data.sections.length === 0) {
		return (
			<>
				<EmptyState
					header={formatMessage(messages.emptyStateHeader)}
					description={formatMessage(
						fg('jira-issue-terminology-refresh-m3')
							? emptyStateDescriptionIssueTermRefreshGalaxia
							: messages.emptyStateDescription,
					)}
					imageUrl={RecentlyViewedImageUrl}
					primaryAction={
						<LinkWithAnalytics
							actionSubjectId="recentlyViewedBrowseProjectsButton"
							appearance="primary"
							buttonLink
							href="/jira/projects"
						>
							<Text as="span" color="color.text.inverse">
								{formatMessage(viewAllProjectsGalaxia)}
							</Text>
						</LinkWithAnalytics>
					}
				/>
				<EndInteractionsTracker transitionKey={WORKEDON_TAB_KEY} renderState={EMPTY_STATE} />
			</>
		);
	}

	const numberOfItemsInAllSections = data.sections.reduce(sumLength, 0);

	const footer =
		numberOfItemsInAllSections > MAX_ITEMS_PER_PAGE && onLoadMoreClick ? (
			<TabFooter>
				<ButtonWithAnalytics
					actionSubjectId="recentlyViewedLoadMoreButton"
					onClick={onLoadMoreClick}
				>
					{formatMessage(messages.loadMore)}
				</ButtonWithAnalytics>
			</TabFooter>
		) : null;

	return (
		<>
			{reloading && <ReloadingSpinner />}
			<ItemList
				sections={trimItemsBySection(props.maxItems, data.sections)}
				onItemClick={handleClick}
			/>
			{footer}
			<EndInteractionsTracker transitionKey={WORKEDON_TAB_KEY} renderState={SUCCESS_STATE} />
		</>
	);
};

export default WorkedOnTab;
